/**
 * Utility that replaces definitions of a record.
 *
 * Example:
 * ```ts
 * type Original = { a: number, b: string };
 * type Derivative = Overwrite<Original, { b: number }>;
 * // Derivative will be { a: number, b: number }
 * ```
 */
export type Overwrite<
  A extends Record<string, unknown>,
  B extends Partial<Record<keyof A, unknown>>
> = {
  [K in Exclude<keyof A, keyof B>]: A[K];
} & {
  [K in keyof B]: B[K];
};

/**
 * Return a new object containing the same keys of the input one, except from the ones specified in the exclusion
 * list.
 * @param source An object.
 * @param keys Keys to remove from the input object.
 */
export function omit<T, K extends keyof T>(
  source: T,
  keys: Array<K>
): Omit<T, K> {
  const result = { ...source };
  for (const k of keys) {
    delete result[k];
  }
  return result as Omit<T, K>;
}

export function typedObjectKeys<TStructure extends object>(
  obj: TStructure
): Array<keyof TStructure> {
  return Object.keys(obj) as Array<keyof TStructure>;
}

export function typedObjectValues<TStructure extends object>(
  obj: TStructure
): Array<TStructure[keyof TStructure]> {
  return Object.values(obj) as Array<TStructure[keyof TStructure]>;
}

export function typedObjectEntries<TStructure extends object>(
  obj: TStructure
): Array<{ [K in keyof TStructure]: [K, TStructure[K]] }[keyof TStructure]> {
  return Object.entries(obj) as Array<
    { [K in keyof TStructure]: [K, TStructure[K]] }[keyof TStructure]
  >;
}

export function typedObjectFromEntries<TKey extends string, TValue>(
  arr: Array<[TKey, TValue]>
): { [K in TKey]: TValue } {
  return Object.fromEntries(arr) as { [K in TKey]: TValue };
}
